/**
 * money.ts — canonical helpers for transaction amount handling.
 *
 * Sign convention:
 *   - Negative amounts = expense (money leaving the account).
 *   - Positive amounts = income (money entering the account).
 *
 * Mixing inline `Math.abs(t.amount)` with rolling sums silently
 * inverts a total in one widget while leaving others correct. Route
 * sign-aware behavior through these helpers so the convention lives
 * in one file. CLAUDE.md "money signs" section is the source of
 * truth for this rule.
 */

export type TxnLike = { amount?: number | null };

/** Plain absolute-value formatter, no sign character, no currency symbol. */
export function fmtMoney(n: number, fractionDigits: 0 | 2 = 0): string {
  return Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

/** Returns "+$1,234.56" or "−$1,234.56" preserving the input's sign. */
export function signedMoney(n: number, fractionDigits: 0 | 2 = 2): string {
  const sign = n >= 0 ? "+" : "−";
  return `${sign}$${fmtMoney(n, fractionDigits)}`;
}

/**
 * Sum of the absolute value of expense rows (amount < 0).
 * Use this when you want a positive expense total — never write the
 * `Math.abs(t.amount)` reduce by hand.
 */
export function expenseTotal(txns: TxnLike[]): number {
  return (txns || [])
    .filter((t) => (t.amount ?? 0) < 0)
    .reduce((s, t) => s + Math.abs(t.amount ?? 0), 0);
}

/** Sum of income rows (amount > 0). Returned as a positive number. */
export function incomeTotal(txns: TxnLike[]): number {
  return (txns || [])
    .filter((t) => (t.amount ?? 0) > 0)
    .reduce((s, t) => s + (t.amount ?? 0), 0);
}

/** Compact absolute amount for tight labels: "$950", "$1.2k", "$30k", "$1.4M". */
export function compactMoney(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e6) return `$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e4) return `$${Math.round(a / 1e3)}k`;
  if (a >= 1e3) return `$${(a / 1e3).toFixed(1)}k`;
  return `$${Math.round(a)}`;
}
